import { useState } from "react";
import { Volume2, ChevronLeft, ChevronRight } from "lucide-react";

// Color per role — same scheme as SentenceBuilderGame and Lesson breakdown
const ROLE_COLORS = {
  Subject: "bg-sky/30 text-sky-light border-sky-light",
  Verb: "bg-sage/30 text-sage-light border-sage",
  Object: "bg-gold-500/30 text-paper border-gold-500",
  Article: "bg-gold-500/30 text-gold-300 border-gold-500",
  Preposition: "bg-rust/30 text-paper border-rust-light",
  Adjective: "bg-rust/30 text-paper border-rust-light",
};

function speak(text) {
  if ("speechSynthesis" in window) {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "en-US";
    u.rate = 0.85;
    window.speechSynthesis.speak(u);
  }
}

// Try to color-code each word in an example string by matching against the lesson's role hints.
// We use a tiny heuristic when no per-word data exists: match against known role keywords.
function autoBreakdown(sentence, lesson) {
  const words = sentence.split(/\s+/);
  const roleByWord = {};
  if (lesson.words) {
    lesson.words.forEach((w) => { roleByWord[w.word.toLowerCase()] = w.role; });
  }
  // Very simple fallback heuristics
  const articles = ["a", "an", "the"];
  const verbsLikely = ["is", "are", "am", "was", "were", "fly", "flies", "run", "runs", "eat", "eats", "see", "sees", "like", "likes", "love", "loves", "play", "plays", "sleep", "sleeps", "drink", "drinks", "read", "reads", "watch", "watches", "ride", "rides", "have", "has", "want", "wants", "open", "opens", "close", "closes", "sing", "sings", "dance", "dances", "jump", "jumps", "swim", "swims", "walk", "walks", "smile", "smiles", "laugh", "laughs", "cry", "cries", "buy", "buys", "throw", "throws", "catch", "catches", "bark", "barks", "hoot", "hoots", "buzz", "buzzes", "crawl", "crawls", "rises", "rise"];
  const subjectsLikely = ["i", "you", "he", "she", "it", "we", "they"];

  return words.map((w) => {
    const lower = w.toLowerCase().replace(/[.,!?]$/, "");
    let role = roleByWord[lower];
    if (!role) {
      if (articles.includes(lower)) role = "Article";
      else if (verbsLikely.includes(lower)) role = "Verb";
      else if (subjectsLikely.includes(lower)) role = "Subject";
      else role = "Object"; // best guess for remaining nouns
    }
    // Subjects in the middle of a sentence are usually objects
    return { text: w, role };
  });
}

export default function LessonExamples({ lesson, dark = true }) {
  const [idx, setIdx] = useState(0);
  const examples = lesson.examples || [];
  if (examples.length === 0) return null;

  const current = examples[idx];
  const broken = typeof current === "string" ? autoBreakdown(current, lesson) : current;

  function prev() { setIdx((i) => (i - 1 + examples.length) % examples.length); }
  function next() { setIdx((i) => (i + 1) % examples.length); }

  return (
    <div className={`rounded-3xl p-5 ${dark ? "bg-white/10 border border-white/20" : "bg-white border border-gray-200"}`}>
      <div className="flex items-center justify-between mb-3">
        <p className={`text-xs uppercase tracking-wider font-bold ${dark ? "text-white/50" : "text-gray-500"}`}>
          More examples ({idx + 1}/{examples.length})
        </p>
        <div className="flex items-center gap-1">
          <button onClick={prev} className={`p-1.5 rounded-full transition ${dark ? "hover:bg-white/10 text-white/70" : "hover:bg-gray-100 text-gray-600"}`}>
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={next} className={`p-1.5 rounded-full transition ${dark ? "hover:bg-white/10 text-white/70" : "hover:bg-gray-100 text-gray-600"}`}>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* The colored sentence */}
      <div className="flex flex-wrap items-center justify-center gap-1.5 mb-3 min-h-[80px] py-2">
        {broken.map((w, i) => (
          <span
            key={i}
            className={`px-2.5 py-1.5 rounded-lg border-2 text-base font-bold ${ROLE_COLORS[w.role] || (dark ? "bg-white/10 text-white border-white/20" : "bg-gray-100 text-gray-800 border-gray-300")}`}
            title={w.role}
          >
            {w.text}
          </span>
        ))}
      </div>

      {/* Listen + role legend */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button
          onClick={() => speak(typeof current === "string" ? current : current.map((w) => w.text).join(" "))}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition ${
            dark ? "bg-white/10 hover:bg-white/20 text-white" : "bg-ink-900 text-white hover:bg-ink-900"
          }`}
        >
          <Volume2 className="w-3.5 h-3.5" /> Listen
        </button>
        <div className="flex items-center gap-1.5 flex-wrap">
          {[...new Set(broken.map((w) => w.role))].map((r) => (
            <span key={r} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${ROLE_COLORS[r] || ""}`}>
              {r}
            </span>
          ))}
        </div>
      </div>

      {/* Dot pagination */}
      <div className="flex justify-center gap-1 mt-3">
        {examples.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === idx ? "w-6 bg-gold-500" : "w-1.5 bg-white/30"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
